import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { baseApi } from "./baseApi";
import { engineApi } from "./engineApi";
import { CURRENT_BROKER_NAME } from "@/config/logos";

export const usePostCreateStrategy = () => {
  return useMutation({
    mutationKey: ["create-strategy"],
    mutationFn: async ({
      symbol,
      timeframe,
      strategy_name,
      user_id,
    }: {
      symbol: string;
      timeframe: string;
      strategy_name: string;
      user_id: string;
    }) => {
      const queryParams = new URLSearchParams({
        user_id,
        symbol,
        timeframe,
        strategy_name,
      }).toString();

      const response = await baseApi.post(`/build_new_strategy?${queryParams}`);
      return response;
    },
  });
};

export const usePostCopyStrategy = () => {
  return useMutation({
    mutationKey: ["copy-strategy"],
    mutationFn: async ({
      userId,
      key,
      symbol,
    }: {
      userId: string;
      key: string;
      symbol?: string;
    }) => {
      const response = await baseApi.post(
        `/copy_strategy?user_id=${userId}&strategy_key=${key}${
          symbol ? `&symbol=${symbol}` : ""
        }`
      );
      return response;
    },
  });
};

export const useGetLiveDeploymentStats = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_live_deployments_aggregated_stats", user_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_live_deployments_aggregated_stats?user_id=${user_id}`
          );
          return response;
        },
  });
};

export const useGetAllLiveDeployment = (
  user_id?: string,
  broker_id?: string,
  page?: number
) => {
  return useQuery({
    queryKey: ["get_all_live_deployments", user_id, broker_id, page],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_all_live_deployments?user_id=${user_id}&broker_id=${broker_id}&page=${page}&size=6`
          );
          return response;
        },
  });
};

export const useGetMonitorDeploymentStats = (
  user_id?: string,
  broker_id?: string
) => {
  return useQuery({
    queryKey: ["get_monitor_deployments_stats", user_id, broker_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_monitor_deployments_stats?user_id=${user_id}${
              broker_id ? `&broker_id=${broker_id}` : ""
            }`
          );
          return response;
        },
  });
};

export const usePostUndeployLiveStrategy = () => {
  return useMutation({
    mutationKey: ["undeploy_live_strategy"],
    mutationFn: async ({
      userId,
      strategy_id,
    }: {
      userId: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.post(
        `/undeploy_live_strategy?user_id=${userId}&strategy_id=${strategy_id}`
      );
      return response;
    },
  });
};

export const usePostChangeStrategyName = () => {
  return useMutation({
    mutationKey: ["change_strategy_name"],
    mutationFn: async ({
      userId,
      strategy_id,
      strategy_name,
    }: {
      userId: string;
      strategy_id: string;
      strategy_name: string;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_name?user_id=${userId}&strategy_id=${strategy_id}&strategy_name=${strategy_name}`
      );
      return response;
    },
  });
};

export const usePostDeployVirtualStrategy = () => {
  return useMutation({
    mutationKey: ["deploy_virtual_strategy"],
    mutationFn: async ({
      userId,
      strategy_id,
    }: {
      userId: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.post(
        `/deploy_virtual_strategy?user_id=${userId}&strategy_id=${strategy_id}`
      );
      return response;
    },
  });
};

export const useGetAllLiveDeploymentOrderStatus = () => {
  return useMutation({
    mutationKey: ["get_live_deployments_order_status"],
    mutationFn: async ({
      user_id,
      data,
    }: {
      user_id?: string;
      data?: any[];
    }) => {
      const url = new URL(
        "/get_live_deployments_order_status",
        baseApi.defaults.baseURL
      );
      if (user_id) url.searchParams.append("user_id", user_id);

      if (data?.length) {
        const strategyIds = data
          .map((inst) => inst.deployment_meta.strategy.id)
          .join(",");
        url.searchParams.append("strategy_ids", strategyIds);
      } else {
        return;
      }

      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetLogos = () => {
  return useMutation({
    mutationKey: ["get_logos"],
    mutationFn: async ({ symbols }: { symbols?: any[] }) => {
      const url = new URL("/get_logos", baseApi.defaults.baseURL);
      url.searchParams.append("server", `${CURRENT_BROKER_NAME}.trylevel2.com`);
      if (symbols) url.searchParams.append("symbols", symbols?.toString());
      else return;
      const response = await baseApi.get(url.toString());
      return response;
    },
    retry: 0,
    retryDelay: 1000,
  });
};

export const useGetStrategyTrainerTPSL = () => {
  return useMutation({
    mutationKey: ["strategy_trainer_try_tpsl_v2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      tp,
      sl,
    }: {
      user_id: string;
      strategy_id: string;
      tp: string;
      sl: string;
    }) => {
      const response = await baseApi.get(
        `/strategy_trainer_try_tpsl_v2?user_id=${user_id}&strategy_id=${strategy_id}&tp=${tp}&sl=${sl}`
      );
      return response;
    },
  });
};
export const useGetStrategyTrainerAction = () => {
  return useMutation({
    mutationKey: ["strategy_trainer_try_action_v2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      action,
    }: {
      user_id: string;
      strategy_id: string;
      action: string;
    }) => {
      const response = await baseApi.get(
        `/strategy_trainer_try_action_v2?user_id=${user_id}&strategy_id=${strategy_id}&action=${action}`
      );
      return response;
    },
  });
};

export const useGetPL = (
  user_id?: string,
  type?: string,
  deployment_id?: string
) => {
  return useQuery({
    queryKey: ["get_p_l_live_deployed_strategies_v2", user_id, type],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_p_l_${type}_deployed_strategies_v2?user_id=${user_id}${
          deployment_id ? `&deployment_id=${deployment_id}` : ""
        }`
      );
      return response;
    },
    enabled: !!user_id,
    refetchOnMount: false, // Always fetch when component mounts
    refetchInterval: 5000, //Polling data every 3 sec to get live data
  });
};

export const useGetExpiredStrategies = () => {
  return useMutation({
    mutationKey: ["get_live_expired_strategies"],
    mutationFn: async ({
      user_id,
      type,
      offset,
    }: {
      user_id?: string;
      type?: string;
      offset?: number;
    }) => {
      const response = await baseApi.get(
        `/get_${type}_expired_strategies?user_id=${user_id}&offset=${
          offset ? offset : 0
        }`
      );
      return response;
    },
  });
};

export const useGetLiveStrategiesVolume = () => {
  return useMutation({
    mutationKey: ["get_live_deployment_strategies_volume"],
    mutationFn: async ({
      user_id,
      data,
    }: {
      user_id?: string;
      data?: any[];
    }) => {
      const url = new URL(
        "/get_live_deployment_strategies_volume",
        baseApi.defaults.baseURL
      );
      if (user_id) url.searchParams.append("user_id", user_id);

      if (data?.length) {
        const strategyIds = data
          .map((inst) => inst.deployment_meta.strategy.id)
          .join(",");
        url.searchParams.append("strategy_ids", strategyIds);
      } else {
        return;
      }

      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetExpiredPLs = () => {
  return useMutation({
    mutationKey: ["get_virtual_deps_pnl"],
    mutationFn: async ({
      user_id,
      type,
      deploymentIds,
    }: {
      user_id?: string;
      type?: string;
      deploymentIds?: string;
    }) => {
      const url = new URL(`/get_${type}_deps_pnl`, baseApi.defaults.baseURL);

      if (deploymentIds) {
        url.searchParams.append("deployment_ids", deploymentIds);
      }
      if (user_id) {
        url.searchParams.append("user_id", user_id);
      }

      const response = await baseApi.get(url.toString());
      return response.data;
    },
  });
};

export const useGetStrategiesByUser = ({
  user_id,
  page,
}: {
  user_id?: string;
  page?: string;
}) => {
  return useQuery({
    queryKey: ["get_strategies_by_user", user_id, page],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategies_by_user?user_id=${user_id}&page=${
          page || 1
        }&is_copied=0&size=6`
      );
      return response;
    },
    enabled: !!user_id,
  });
};

export const useGetStrategyPerformanceStats = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_strategy_performance_stats", user_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_performance_stats?user_id=${user_id}`
      );
      return response;
    },
    enabled: !!user_id,
  });
};

export const usePostUnDeployNotificationDeployment = () => {
  return useMutation({
    mutationKey: ["undeploy_notification_deployment"],
    mutationFn: async ({
      user_id,
      deployment_id,
    }: {
      user_id?: string;
      deployment_id?: string;
    }) => {
      const response = await baseApi.post(
        `/undeploy_notification_deployment?user_id=${user_id}&deployment_id=${deployment_id}`
      );
      return response.data;
    },
  });
};

export const usePostDeleteMultipleStrategies = () => {
  return useMutation({
    mutationKey: ["delete_multiple_strategies"],
    mutationFn: async ({
      user_id,
      strategy_ids,
    }: {
      user_id?: string;
      strategy_ids?: string;
    }) => {
      const response = await baseApi.post(
        `/delete_multiple_strategies?user_id=${user_id}&strategy_ids=${strategy_ids}`
      );
      return response.data;
    },
  });
};

export const usePostPublishStrategies = () => {
  return useMutation({
    mutationKey: ["profile_publish_strategy"],
    mutationFn: async ({
      user_id,
      strategy_id,
      publish_state,
    }: {
      user_id?: string;
      strategy_id?: string;
      publish_state?: string;
    }) => {
      const response = await baseApi.post(
        `/profile_publish_strategy?user_id=${user_id}&strategy_id=${strategy_id}&publish_state=${publish_state}`
      );
      return response.data;
    },
  });
};

export const useGetStrategyDetails = (
  user_id?: string,
  strategy_id?: string
) => {
  return useQuery({
    queryKey: ["get_strategy_details", user_id, strategy_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_details?user_id=${user_id}&strategy_id=${strategy_id}`
      );
      return response;
    },
    enabled: !!user_id && !!strategy_id,
    staleTime: 1,
    refetchOnWindowFocus: false,
  });
};

export const useGetStrategyDetailsMutation = () => {
  return useMutation({
    mutationKey: ["get_strategy_details"],
    mutationFn: async ({
      user_id,
      strategy_id,
    }: {
      user_id: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_strategy_details?user_id=${user_id}&strategy_id=${strategy_id}`
      );
      return response;
    },
  });
};

export const usePostGetBacktestData = () => {
  return useMutation({
    mutationKey: ["backtest_data_task"],
    mutationFn: async ({ strategy_keygen }: { strategy_keygen: string }) => {
      const formData = new URLSearchParams();
      formData.append("strategy_keygen", strategy_keygen);

      const response = await engineApi.post(`/backtest_data_task`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      return response;
    },
    retry: 10,
    retryDelay: 1000,
  });
};

export const usePostBacktestTaskResult = () => {
  return useMutation({
    mutationKey: ["backtest_task_result"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.post(
        `/backtest_task_result?task_id=${task_id}`
      );
      return response;
    },
  });
};

export const usePostBacktestQueue = () => {
  return useMutation({
    mutationKey: ["backtest_queue"],
    mutationFn: async ({ formData }: { formData: any }) => {
      const response = await engineApi.post(`/backtest_queue`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response;
    },
  });
};

export const usePostBacktestStatus = () => {
  return useMutation({
    mutationKey: ["backtest_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/backtest_status?task_id=${task_id}`
      );
      return response;
    },
  });
};

export const useGetBacktestStatus = () => {
  return useMutation({
    mutationKey: ["get_backtest_status"],
    mutationFn: async ({ strategy_keygen }: { strategy_keygen: string }) => {
      const formData = new URLSearchParams();
      formData.append("strategy_keygen", strategy_keygen);
      const response = await engineApi.get(
        `/get_backtest_status?strategy_keygen=${strategy_keygen}`
      );

      return response;
    },
  });
};

// get_all_report_versions
export const useGetAllReportVersions = () => {
  return useMutation({
    mutationKey: ["get_all_report_versions"],
    mutationFn: async ({
      user_id,
      strategy_id,
    }: {
      user_id: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_all_report_versions?user_id=${user_id}&strategy_id=${strategy_id}`
      );
      return response;
    },
  });
};

export const usePostBacktestTimeframeQueue = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queue"],
    mutationFn: async ({
      user_id,
      strategy_id,
      formData,
    }: {
      user_id: string;
      strategy_id: string;
      formData: any;
    }) => {
      const response = await engineApi.post(
        `/backtest_timeframe_queue?user_id=${user_id}&strategy_id=${strategy_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      return response;
    },
  });
};

export const usePostBacktestTimeframeQueueV2 = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queuev2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      formData,
    }: {
      user_id: string;
      strategy_id: string;
      formData: any;
    }) => {
      const response = await engineApi.post(
        `/backtest_timeframe_queue_v2?user_id=${user_id}&strategy_id=${strategy_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      return response;
    },
  });
};

export const usePostMediumBacktest = () => {
  return useMutation({
    mutationKey: ["medium_backtest"],
    mutationFn: async ({ formData }: { formData: any }) => {
      const response = await engineApi.post(`/medium_backtest`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response;
    },
  });
};

export const usePostChangeTimeFrame = () => {
  return useMutation({
    mutationKey: ["change_strategy_timeframe"],
    mutationFn: async ({
      user_id,
      strategy_id,
      timeframe,
    }: {
      user_id: string;
      strategy_id: string;
      timeframe: string;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_timeframe?user_id=${user_id}&strategy_id=${strategy_id}&timeframe=${timeframe}`
      );
      return response;
    },
  });
};
export const useGetMediumBacktestStatus = () => {
  return useMutation({
    mutationKey: ["get_backtest_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/get_backtest_status?task_id=${task_id}`
      );
      return response;
    },
    retry: 10,
  });
};

export const useGetBacktestTimeframeStatus = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/backtest_timeframe_status?task_id=${task_id}`
      );
      return response;
    },
    retry: 3,
  });
};

export const usePostChangeStrategy = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queue"],
    mutationFn: async ({
      user_id,
      strategy_id,
      formData,
    }: {
      user_id: string;
      strategy_id: string;
      formData: any;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_via_trainer?user_id=${user_id}&strategy_id=${strategy_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      return response;
    },
  });
};

export const useGetUserStrategyNames = () => {
  return useMutation({
    mutationKey: ["get_user_strategy_names"],
    mutationFn: async ({ user_id }: { user_id?: string }) => {
      const response = await baseApi.get(
        `/get_user_strategy_names?user_id=${user_id ?? "4VXr2i"}`
      );
      return response;
    },
  });
};
export const useGetCopyUserStrategy = () => {
  return useMutation({
    mutationKey: ["copy_user_strategy"],
    mutationFn: async ({
      user_id,
      keygen_original,
      keygen_current,
    }: {
      user_id?: string;
      keygen_original: string;
      keygen_current: string;
    }) => {
      const response = await baseApi.get(
        `/copy_user_strategy?user_id=${user_id}&keygen_original=${keygen_original}&keygen_current=${keygen_current}`
      );
      return response;
    },
  });
};

export const usePostCommonIndicators = () => {
  return useMutation({
    mutationKey: ["indicator_value"],
    mutationFn: async ({
      indicator,
      formData,
    }: {
      indicator: string;
      formData: FormData;
    }) => {
      const response = await engineApi.post(
        `/indicator_value?indicator=${indicator}`,
        formData
      );
      return response?.data;
    },
  });
};

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["get_strategy_categories"],
    queryFn: async () => {
      const response = await baseApi.get(`/get_strategy_categories`);
      return response;
    },
  });
};

export const useGetCheckForex = (symbol?: string) => {
  return useQuery({
    queryKey: ["check_symbol_forex", symbol],
    queryFn: async () => {
      try {
        const response = await baseApi.get(
          `/check_symbol_forex?symbol=${symbol}`
        );
        return response;
      } catch {
        console.error("failed get recent strategies");
      }
    },
    enabled: !!symbol,
  });
};
